import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useSelector } from 'react-redux'
import prepareHeaders from '../utils/prepareHeaders'
import { getSelectedCustomerId, getSelectedOrFirstCustomerId } from '../selectors/customers'
import addParameters from '../utils/addParameters'

const base = '/secure'

export const SECURE_POSTURE_TAG = 'SecurePosture'
export const SECURE_CHECK_TAG = 'SecureCheck'
export const SECURE_POSTURE_CHECK_NOTES_TAG = 'SecurePostureCheckNotes'

export const getSecurePostureTag = (customerId) => ({ type: SECURE_POSTURE_TAG, id: String(customerId) })

export const getSecureCheckTag = (customerId, checkType) => ({
  type: SECURE_CHECK_TAG,
  id: `${customerId}-${checkType}`,
})

export const getSecurePostureCheckNotesTag = (customerId, checkId) => ({
  type: SECURE_POSTURE_CHECK_NOTES_TAG,
  id: `${customerId}-${checkId}`,
})

export const getSecurePostureCustomerNotesTag = (customerId) => ({
  type: SECURE_POSTURE_CHECK_NOTES_TAG,
  id: `customer-${customerId}`,
})

const getPostureCheckNotesPath = ({ customerId, checkId, noteId }) => {
  const noteSegment = noteId != null ? `/${noteId}` : ''
  return `${base}/posture/${customerId}/check/${encodeURIComponent(checkId)}/notes${noteSegment}`
}

const getPostureCustomerNotesPath = (customerId) => `${base}/posture/${customerId}/notes`

const getNotesInvalidationTags = (customerId, checkId) => [
  getSecurePostureCheckNotesTag(customerId, checkId),
  getSecurePostureCustomerNotesTag(customerId),
]

export const secureApi = createApi({
  reducerPath: 'securePosture',
  tagTypes: [SECURE_POSTURE_TAG, SECURE_CHECK_TAG, SECURE_POSTURE_CHECK_NOTES_TAG],
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getCheck: builder.query({
      keepUnusedDataFor: Infinity,
      providesTags: (_, __, { checkType, customerId }) => [getSecureCheckTag(customerId, checkType)],
      query: ({ checkType, customerId, mfaUserDataOnly = false, mfaTenantDataOnly = false, caPolicyId }) =>
        addParameters(`${base}/check/${checkType}`, { customerId, mfaUserDataOnly, mfaTenantDataOnly, caPolicyId }),
    }),
    getSpoSharingChecks: builder.query({
      keepUnusedDataFor: Infinity,
      query: ({ customerId }) => addParameters(`${base}/check/spo-sharing`, { customerId }),
    }),
    getSecurityPosture: builder.query({
      keepUnusedDataFor: Infinity,
      providesTags: (_, __, customerId) => [getSecurePostureTag(customerId)],
      query: (customerId = '*') => `${base}/posture/${customerId}`,
    }),
    getAiReadinessReport: builder.query({
      query: (customerId) => `${base}/ai-readiness/${customerId}`,
    }),
    getConditionalAccessPolicies: builder.query({
      query: (customerId) => `${base}/policy/${customerId}/policies/cap`,
    }),
    getConditionalAccessPolicy: builder.query({
      query: ({ customerId, policyId }) => `${base}/policy/${customerId}/policies/cap/${policyId}`,
    }),
    getPostureCheckNotes: builder.query({
      providesTags: (_, __, { customerId, checkId }) => [getSecurePostureCheckNotesTag(customerId, checkId)],
      query: ({ customerId, checkId }) => getPostureCheckNotesPath({ customerId, checkId }),
      transformResponse: (response) => (Array.isArray(response) ? response : []),
    }),
    getCustomerPostureCheckNotes: builder.query({
      providesTags: (_, __, customerId) => [getSecurePostureCustomerNotesTag(customerId)],
      query: (customerId) => getPostureCustomerNotesPath(customerId),
      transformResponse: (response) => (Array.isArray(response) ? response : []),
    }),
    createPostureCheckNote: builder.mutation({
      invalidatesTags: (_, __, { customerId, checkId }) => getNotesInvalidationTags(customerId, checkId),
      query: ({ customerId, checkId, payload }) => ({
        url: getPostureCheckNotesPath({ customerId, checkId }),
        method: 'POST',
        body: payload,
      }),
    }),
    updatePostureCheckNote: builder.mutation({
      invalidatesTags: (_, __, { customerId, checkId }) => getNotesInvalidationTags(customerId, checkId),
      query: ({ customerId, checkId, noteId, payload }) => ({
        url: getPostureCheckNotesPath({ customerId, checkId, noteId }),
        method: 'PUT',
        body: payload,
      }),
    }),
    deletePostureCheckNote: builder.mutation({
      invalidatesTags: (_, __, { customerId, checkId }) => getNotesInvalidationTags(customerId, checkId),
      query: ({ customerId, checkId, noteId }) => ({
        url: getPostureCheckNotesPath({ customerId, checkId, noteId }),
        method: 'DELETE',
      }),
      async onQueryStarted({ customerId, checkId, noteId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          secureApi.util.updateQueryData('getPostureCheckNotes', { customerId, checkId }, (draft) => {
            const index = draft.findIndex((note) => note.id === noteId)
            if (index >= 0) {
              draft.splice(index, 1)
            }
          }),
        )
        const customerPatchResult = dispatch(
          secureApi.util.updateQueryData('getCustomerPostureCheckNotes', customerId, (draft) => {
            const index = draft.findIndex((note) => note.id === noteId)
            if (index >= 0) {
              draft.splice(index, 1)
            }
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
          customerPatchResult.undo()
        }
      },
    }),
    updatePostureStatus: builder.mutation({
      invalidatesTags: (_, __, { customerId }) => [getSecurePostureTag(customerId)],
      query: ({ customerId, checkId, status, statusDetails }) => ({
        url: `${base}/posture/${customerId}/status`,
        method: 'PUT',
        body: { checkId, status, statusDetails },
      }),
    }),
    updateDefenderPolicyState: builder.mutation({
      query: ({ customerId, userId, type, state, policy, rule }) => ({
        url: `${base}/policies/defenders`,
        method: 'PUT',
        params: { customerId, userId, type },
        body: { type, state, policy, rule },
      }),
    }),
  }),
})

export const {
  useGetSecurityPostureQuery,
  useLazyGetSecurityPostureQuery,
  useGetAiReadinessReportQuery,
  useGetConditionalAccessPoliciesQuery,
  useLazyGetConditionalAccessPoliciesQuery,
  useGetConditionalAccessPolicyQuery,
  useLazyGetConditionalAccessPolicyQuery,
  useGetCheckQuery,
  useLazyGetCheckQuery,
  useGetPostureCheckNotesQuery,
  useGetCustomerPostureCheckNotesQuery,
  useCreatePostureCheckNoteMutation,
  useUpdatePostureCheckNoteMutation,
  useDeletePostureCheckNoteMutation,
  useUpdatePostureStatusMutation,
  useUpdateDefenderPolicyStateMutation,
  useGetSpoSharingChecksQuery,
} = secureApi

const MFA_CONDITIONAL_ACCESS_CONFIGURE_TYPES = new Set(['adminmfa', 'usermfa'])

export const getPolicyParameters = (customerId, checkType = 'adminmfa') => ({
  checkType,
  customerId,
  mfaUserDataOnly: false,
  mfaTenantDataOnly: MFA_CONDITIONAL_ACCESS_CONFIGURE_TYPES.has(checkType),
})

export const policyResponseSelector = (result) => {
  const { data, ...rest } = result

  if (!data) {
    return {
      ...rest,
      policyConfigurations: {},
      policiesList: [],
    }
  }

  const policyConfigurations = data.configure ?? {}

  return {
    ...rest,
    policyConfigurations,
    policiesList: policyConfigurations.data ?? [],
  }
}

export const useGetCustomerPolicies = () => {
  const customerId = useSelector(getSelectedCustomerId)
  const { data: policiesList = [], ...rest } = useGetConditionalAccessPoliciesQuery(customerId, {
    skip: !customerId,
  })

  return {
    data: policiesList,
    ...rest,
  }
}

export const useGetCustomerCheck = (checkType, queryOptions = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  // If checkType is not provided, the query will be skipped via the skip option. However, we also want to skip if customerId is not available to avoid making a request with an invalid customerId
  const shouldSkip = Boolean(queryOptions.skip) || !customerId || !checkType

  return useGetCheckQuery(
    { checkType, customerId },
    {
      ...queryOptions,
      skip: shouldSkip,
    },
  )
}

export const useGetCustomerSecurityPosture = (supportAllCompanies = true) => {
  const customerId = useSelector(supportAllCompanies ? getSelectedCustomerId : getSelectedOrFirstCustomerId)

  return useGetSecurityPostureQuery(customerId)
}

export const useGetCustomerSecurityPostureChecks = () => {
  const result = useGetCustomerSecurityPosture(false)
  const { data } = result

  if (!data) {
    return result
  }

  const { securityChecks: { allMonitored = [], ignored = [] } = {} } = data

  return {
    ...result,
    data: [...allMonitored, ...ignored],
  }
}

export const useGetCustomerPostureCheckNotes = (queryOptions = {}) => {
  const customerId = useSelector(getSelectedCustomerId)
  const shouldSkip = Boolean(queryOptions.skip) || !customerId

  return useGetCustomerPostureCheckNotesQuery(customerId, {
    ...queryOptions,
    skip: shouldSkip,
  })
}
